import { useState } from 'react';
import { toast } from 'sonner';
import {
  FileSpreadsheet, FileSignature, Upload, CheckSquare, Paperclip, Download, FileCheck2, RotateCcw,
  Pencil,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import {
  useGenerateFormPdf,
  type FormSubmissionEntry,
  type UploadSubmissionEntry,
  type DocumentSubmissionEntry,
  type TodoSubmissionEntry,
} from '../../hooks/onboardingOps';
import { ApiError } from '../../lib/api';
import { Card, CardSection } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { formatDate } from '../../lib/dateFmt';
import { ItemStatusPill } from '../../components/onboarding/itemStatus';
import { FormAnswers } from '../../components/onboarding/PersonFormSubmissions';
import { AmendAnswersModal } from '../../components/onboarding/AmendAnswersModal';
import type { FieldValue } from '../../components/onboarding/formfields/formValues';

export function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-fg-dim">{title}</h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

export function FormItem({ personId, entry }: { personId: string; entry: FormSubmissionEntry }) {
  const [editing, setEditing] = useState(false);
  const pct =
    entry.progress && entry.progress.required_total > 0
      ? Math.round((entry.progress.required_done / entry.progress.required_total) * 100)
      : null;
  const hasAnswers = entry.state !== 'not_started';
  return (
    <Card>
      <CardSection>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 shrink-0 text-fg-muted" />
            <span className="truncate font-medium text-fg">{entry.title}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {entry.state === 'in_progress' ? (
              <Pill tone="warning" size="sm">In progress{pct !== null ? ` · ${pct}%` : ''}</Pill>
            ) : entry.state === 'submitted' ? (
              <Pill tone="success" size="sm">Submitted</Pill>
            ) : (
              <Pill tone="neutral" size="sm">Not started</Pill>
            )}
            <ItemStatusPill status={entry.status} />
          </div>
        </div>
        {entry.updated_at ? (
          <div className="mt-1 text-xs text-fg-dim">
            {entry.state === 'submitted' ? 'Submitted' : 'Edited'} {formatDate(entry.updated_at)}
            {entry.amended_at ? (
              <span className="text-warning-soft-fg"> · corrected by staff {formatDate(entry.amended_at)}</span>
            ) : null}
          </div>
        ) : null}
        {entry.state === 'submitted' ? (
          <div className="flex flex-wrap items-center gap-3">
            <SignedPdfControls personId={personId} entry={entry} />
            <Button
              variant="ghost"
              size="sm"
              iconLeft={<Pencil className="h-3.5 w-3.5" />}
              onClick={() => setEditing(true)}
            >
              Edit answers
            </Button>
          </div>
        ) : null}
        {editing ? (
          <AmendAnswersModal personId={personId} entry={entry} onClose={() => setEditing(false)} />
        ) : null}
        {hasAnswers ? (
          <details className="group mt-2">
            <summary className="cursor-pointer list-none text-sm text-accent hover:underline">
              View answers
            </summary>
            <div className="mt-2 rounded-token-sm border border-line/10 bg-surface-2 p-3">
              <FormAnswers
                personId={personId}
                versionId={entry.form_version_id ?? undefined}
                values={entry.values as Record<string, FieldValue>}
                files={entry.files}
                consent={entry.consent}
              />
            </div>
          </details>
        ) : null}
      </CardSection>
    </Card>
  );
}

/**
 * Generate / download the stored signed-PDF snapshot for a submitted form.
 * Generating stores a copy server-side so HR and the inductee download the
 * exact same document; before that, the download still renders on demand.
 */
function SignedPdfControls({ personId, entry }: { personId: string; entry: FormSubmissionEntry }) {
  const generate = useGenerateFormPdf(personId);
  const href = `/api/admin/onboarding/people/${personId}/forms/${entry.form_id}/signed.pdf`;
  const stored = entry.signed_pdf_generated_at;

  const run = () =>
    generate.mutate(entry.form_id, {
      onSuccess: () => toast.success('Signed PDF stored'),
      onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not generate PDF'),
    });

  if (!stored) {
    return (
      <div className="mt-2">
        <Button
          variant="secondary"
          size="sm"
          iconLeft={<FileCheck2 className="h-3.5 w-3.5" />}
          loading={generate.isPending}
          onClick={run}
        >
          Generate signed PDF
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      <a href={href} className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline">
        <Download className="h-3.5 w-3.5" />
        Download signed PDF
      </a>
      <span className="text-xs text-fg-dim">Stored {formatDate(stored)}</span>
      <Button
        variant="ghost"
        size="sm"
        iconLeft={<RotateCcw className="h-3.5 w-3.5" />}
        loading={generate.isPending}
        onClick={run}
      >
        Regenerate
      </Button>
    </div>
  );
}

export function DocumentItem({ personId, entry }: { personId: string; entry: DocumentSubmissionEntry }) {
  // The route resolves the stored merged PDF (source + signing certificate),
  // rebuilding on demand for documents signed before snapshotting existed.
  const href = `/api/admin/onboarding/people/${personId}/documents/${entry.task_id}/signed.pdf`;
  return (
    <Card>
      <CardSection>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <FileSignature className="h-4 w-4 shrink-0 text-fg-muted" />
            <span className="truncate font-medium text-fg">{entry.title}</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {entry.updated_at ? (
              <span className="text-xs text-fg-dim">{formatDate(entry.updated_at)}</span>
            ) : null}
            <ItemStatusPill status={entry.status} />
          </div>
        </div>
        {entry.signed_at ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline"
            >
              <Download className="h-3.5 w-3.5" />
              Download signed PDF
            </a>
            <span className="text-xs text-fg-dim">Signed {formatDate(entry.signed_at)}</span>
          </div>
        ) : null}
      </CardSection>
    </Card>
  );
}

export function UploadItem({ personId, entry }: { personId: string; entry: UploadSubmissionEntry }) {
  return (
    <Card>
      <CardSection>
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <Upload className="h-4 w-4 shrink-0 text-fg-muted" />
            <span className="truncate font-medium text-fg">{entry.title}</span>
          </div>
          <ItemStatusPill status={entry.status} />
        </div>
        {entry.files.length > 0 ? (
          <div className="mt-2 space-y-1">
            {entry.files.map((f) => (
              <a
                key={f.key}
                href={`/api/admin/onboarding/people/${personId}/files/${f.key}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-sm text-accent hover:underline"
              >
                <Paperclip className="h-3.5 w-3.5" />
                {f.name}
              </a>
            ))}
          </div>
        ) : (
          <div className="mt-1 text-xs text-fg-dim">No files uploaded yet</div>
        )}
      </CardSection>
    </Card>
  );
}

export function TodoItem({ entry }: { entry: TodoSubmissionEntry }) {
  return (
    <Card>
      <CardSection className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <CheckSquare className="h-4 w-4 shrink-0 text-fg-muted" />
          <span className="truncate font-medium text-fg">{entry.title}</span>
        </div>
        <ItemStatusPill status={entry.status} />
      </CardSection>
    </Card>
  );
}
